import React, { useState, useEffect, useRef } from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
  Button,
  Select,
  useToast,
  Flex,
  Switch,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import * as Yup from 'yup';
import { useLocation } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import SavingPlanVariantApi from 'api/savingplanvariant';
import { EditIcon, CloseIcon } from '@chakra-ui/icons';
import InputField from 'components/formComponents/inputField';
import { Modules } from 'config/constant';
import { usePermissions } from 'contexts/PermissionContext';
import { isValidUUID } from 'utils/fetchUtils';
import PageError from 'components/nopermission/PageError';
import { SavingPlanVariantValidationSchema } from 'formValidations/savingPlanVariantFormValidations';

export default function DetailsForm(props) {
  const id = IdFromUrl(window.location.href);
  function IdFromUrl(url) {
    const parts = url.split('/');
    const id = parts[parts.length - 1];
    return id;
  }
  const {
    withId,
    variantData,
    setVariantData,
    fetchVariantDataError,
    setFetchVariantDataError,
  } = props;
  const [disabled, setDisabled] = useState(id);
  const toast = useToast();
  const { getPermissionsForModule } = usePermissions();
  const [variantPermissions, setvariantPermissions] = useState(null);
  const isFetching = useRef(false); // Use ref to track loading state without causing re-renders

  const enable = () => {
    setDisabled(!disabled);
  };

  const fetchData = async () => {
    if (withId && !isValidUUID(id)) {
      setFetchVariantDataError(true);
      return;
    }

    // Avoid fetching if already in progress
    if (isFetching.current) return;

    isFetching.current = true;

    try {
      const response = await SavingPlanVariantApi.getById(id);
      const responseData = response.data;
      console.log('Here is the response from savingplanvariant-', responseData);

      if (responseData.status_code === 200) {
        setFetchVariantDataError(false);

        setVariantData(responseData.data);
      } else {
        setFetchVariantDataError(true);
        setVariantData([]);
      }
    } catch (err) {
      setFetchVariantDataError(true);
      console.log(err);
    } finally {
      isFetching.current = false; // Reset loading state at the end
    }
  };

  const handleSubmit = async (values, actions) => {
    console.log('In habndle submit', values);
    try {
      let response = await SavingPlanVariantApi.Update({
        id,
        ...values,
      });
      response = response.data;
      if (response.status_code === 200) {
        toast({
          title: response.message || 'Successfully Updated',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      } else {
        toast({
          title: response.message || 'Error Updating',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
    } catch (err) {
      console.log(err);
      toast({
        title: 'Error Updating',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
    setDisabled(true);
    actions.setSubmitting(false);
  };

  useEffect(() => {
    const variantPermissions = getPermissionsForModule([
      Modules.SavingPlanVariant,
    ]);
    setvariantPermissions(variantPermissions);
    fetchData();
  }, []);

  function getNameFromId(inputId) {
    for (const obj of props.SavingplanList) {
      if (obj.id === inputId) {
        return obj.name;
      }
    }
    return null; // Return null if ID not found
  }

  console.log('variantPermissions', variantPermissions);

  const initialValues = {
    savingsPlanId: variantData?.saving_plan_id || '',
    category: variantData?.category || '',
    prematureAllowed: variantData?.premature_withdrawal_allowed || false,
    minInvestment: variantData?.min_investment || '',
    maxInvestment: variantData?.max_investment || '',
    period: variantData?.period || '',
    multipleInvestment: variantData?.multiple_investment || '',
    maxInstallmentAllowed: variantData?.max_installment_allowed || '',
    makingChargesDiscount: variantData?.making_charges_discount || '',
    diamondDiscount: variantData?.diamond_discount || 0,
    benefit: variantData?.short_benefit.text || 0,
    isAvailable: variantData?.is_available || false,
    userBankRequired: variantData?.user_bank_required || false,
  };

  return fetchVariantDataError ? (
    <PageError />
  ) : (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
      validationSchema={SavingPlanVariantValidationSchema}>
      {({ isSubmitting, setFieldValue }) => (
        <Form>
          <Flex
            alignItems='center'
            justifyContent='end'
            mb={10}>
            {disabled ? (
              <Tooltip
                label={
                  !variantPermissions?.update
                    ? 'You do not have permission to edit'
                    : undefined
                }
                placement='top'
                shouldWrapChildren
                isDisabled={variantPermissions?.update} // Disables Tooltip if update permission exists
              >
                <EditIcon
                  ml='5'
                  fontSize='large'
                  color={
                    variantPermissions?.update && variantPermissions?.read
                      ? 'black'
                      : 'gray.400'
                  }
                  onClick={
                    variantPermissions?.update && variantPermissions?.read
                      ? enable
                      : undefined
                  } // Only enables click if update permission exists
                  cursor={
                    variantPermissions?.update ? 'pointer' : 'not-allowed'
                  }
                />
              </Tooltip>
            ) : (
              <CloseIcon
                ml='5'
                fontSize='large'
                onClick={() => setDisabled(true)}
                cursor='pointer'
              />
            )}
          </Flex>
          <FormControl>
            <FormLabel>
              Savings Plan: {getNameFromId(variantData?.saving_plan_id)}
            </FormLabel>
          </FormControl>
          <Flex
            direction='row'
            ml={{ base: 'column', md: 'column', lg: 'row' }}
            justifyContent='space-evenly'
            gap={4}>
            <InputField
              label='Minimum Investment'
              name='minInvestment'
              type='number'
              required={true}
              disabled={disabled}
            />
            <InputField
              label='Maximum Investment'
              name='maxInvestment'
              type='number'
              required={true}
              disabled={disabled}
            />
            <InputField
              label='Multiple Investment'
              name='multipleInvestment'
              type='number'
              required={true}
              disabled={disabled}
            />
          </Flex>
          <Flex
            direction='row'
            ml={{ base: 'column', md: 'column', lg: 'row' }}
            justifyContent='space-evenly'
            gap={4}>
            <InputField
              label='Period (in months)'
              name='period'
              type='number'
              required={true}
              placeholder='Enter in Months'
              disabled={disabled}
            />
            <InputField
              label='Max Installment Allowed'
              name='maxInstallmentAllowed'
              type='number'
              required={true}
              placeholder='Enter number of installments allowed'
              disabled={disabled}
            />
          </Flex>
          {getNameFromId(variantData?.saving_plan_id) === 'Diamond' ? (
            <InputField
              label='Diamond Discount Per Carat'
              name='diamondDiscount'
              type='number'
              required={false}
              disabled={disabled}
            />
          ) : (
            <InputField
              label='Making Charges Discount percentage'
              name='makingChargesDiscount'
              type='number'
              required={false}
              disabled={disabled}
            />
          )}
          {/* <TextAreaField
            label='Benefits'
            name='benefit'
            required={true}
            placeholder='Up to 200 Characters'
            maxLength='200'
            disabled={disabled}
          /> */}
          <Flex
            direction='row'
            ml={{ base: 'column', md: 'column', lg: 'row' }}
            justifyContent='space-evenly'
            gap={4}>
            {/* <FormControl mt={3}>
              <FormLabel>
                <Text fontSize='md' fontWeight='50'>
                  Premature Withdrawal Allowed
                </Text>
              </FormLabel>
              <Flex alignItems='center' mt={-2}>
                <Field name='prematureAllowed'>
                  {({ field }) => (
                    <Switch
                      {...field}
                      isChecked={field.value}
                      onChange={(e) =>
                        setFieldValue('prematureAllowed', e.target.checked)
                      }
                      variant={disabled ? 'authNoBorder' : 'auth'}
                      disabled={disabled}
                    />
                  )}
                </Field>
              </Flex>
            </FormControl> */}
            <FormControl mt={3}>
              <FormLabel>
                <Text
                  fontSize='md'
                  fontWeight='50'>
                  Available ?
                </Text>
              </FormLabel>
              <Flex
                alignItems='center'
                mt={-2}>
                <Field name='isAvailable'>
                  {({ field }) => (
                    <Switch
                      {...field}
                      isChecked={field.value}
                      onChange={(e) =>
                        setFieldValue('isAvailable', e.target.checked)
                      }
                      variant={disabled ? 'authNoBorder' : 'auth'}
                      disabled={disabled}
                    />
                  )}
                </Field>
              </Flex>
            </FormControl>
            <FormControl mt={3}>
              <FormLabel>
                <Text fontSize='md' fontWeight='50'>
                  User Bank Required?
                </Text>
              </FormLabel>
              <Flex alignItems='center' mt={-2}>
                <Field name='userBankRequired'>
                  {({ field }) => (
                    <Switch
                      {...field}
                      isChecked={field.value}
                      onChange={(e) =>
                        setFieldValue('userBankRequired', e.target.checked)
                      }
                      variant={disabled ? 'authNoBorder' : 'auth'}
                      disabled={disabled}
                    />
                  )}
                </Field>
              </Flex>
            </FormControl>
          </Flex>
          <Button
            colorScheme='blue'
            mt={5}
            type='submit'
            disabled={disabled || isSubmitting}>
            Save
          </Button>
        </Form>
      )}
    </Formik>
  );
}
